import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import { formatDMY } from "@/lib/dates";
import BeaconTracker from "@/components/BeaconTracker";
import { toast } from "sonner";
import { prefetchRoute } from "@/lib/prefetch";
import { TrendingUp, Radio, Plus, X } from "lucide-react";
import { getPassOn, clearPassOn } from "@/lib/passOn";
import { ContractorCard } from "@/components/ContractorCard";
import { DateRow, TradeChips, FiltersButton } from "@/components/contractors/Controls";

export default function Contractors() {
  const nav = useNavigate();
  const { user } = useAuth();
  useSEO({ title: "Tradies available now — carpenters, sparkies, plumbers & more", path: "/contractors", description: "Find vetted Australian tradies with real availability, ratings and reliability scores. Filter by trade, suburb and date, then send an offer." });
  const isContractor = user?.role === "contractor";
  const isBuilder = user?.role === "builder";
  const [rows, setRows] = useState(null);
  const [trade, setTrade] = useState("");
  const [center, setCenter] = useState("");
  const [availableOn, setAvailableOn] = useState("");
  const [sort, setSort] = useState("");
  const [rateHint, setRateHint] = useState(null);
  const [beaconOpen, setBeaconOpen] = useState(false);
  const [beaconCity, setBeaconCity] = useState("");
  const [beaconMsg, setBeaconMsg] = useState("");
  const [beaconSending, setBeaconSending] = useState(false);
  const [beaconLastSent, setBeaconLastSent] = useState(null);
  const [beaconRefresh, setBeaconRefresh] = useState(0);
  const [passOn, setPassOn] = useState(() => getPassOn());

  useEffect(() => {
    const params = { trade: trade || undefined, available_on: availableOn || undefined, sort: sort || undefined };
    if (center) { params.center = center; params.radius_km = 50; }
    api.get("/contractors", { params }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [trade, center, availableOn, sort]);

  useEffect(() => {
    if (!trade) { setRateHint(null); return; }
    api.get("/analytics/rate-hint", { params: { trade } }).then(({ data }) => setRateHint(data)).catch(() => setRateHint(null));
  }, [trade]);

  const sendBeacon = async () => {
    setBeaconSending(true);
    try {
      const { data } = await api.post("/jobs/beacon", { trade, city: beaconCity.trim() || center || undefined, start_date: availableOn || undefined, message: beaconMsg.trim() || undefined });
      toast.success(`Pinged ${data.sent} tradies · ${data.skipped ? `${data.skipped} skipped` : "all reached"}`);
      setBeaconLastSent({ sent: data.sent, at: new Date().toISOString() });
      setBeaconRefresh((n) => n + 1); setBeaconOpen(false); setBeaconMsg("");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Beacon failed"); }
    finally { setBeaconSending(false); }
  };

  const inSweet = (c) => { const r = Number(c.day_rate || 0); return rateHint && rateHint.low_rate != null && rateHint.high_rate != null && r >= rateHint.low_rate && r <= rateHint.high_rate; };
  const when = availableOn ? `on ${formatDMY(availableOn)}` : "";

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
      <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter" data-testid="contractors-title">{isContractor ? "Know your market" : "Who's on the tools"}</h1>
      {isContractor && <p className="mt-2 text-sm text-zinc-400 max-w-2xl" data-testid="contractors-subtitle-contractor">See what other tradies in your trade charge and how they're rated. Pick a trade to reveal the sweet-spot day rate.</p>}
      {isBuilder && <BeaconTracker user={user} refreshKey={beaconRefresh} onInvited={() => nav("/bookings?tab=offers")} />}
      {isBuilder && passOn && (
        <div className="mt-6 border-2 border-[#FF6A00] bg-black p-4 flex flex-wrap items-center justify-between gap-3" data-testid="pass-on-banner">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-[#FF6A00]">Unfilled offer — pick the next tradie</div>
            <div className="text-sm text-white mt-0.5"><b>{passOn.terms.title}</b> · {passOn.terms.city}{passOn.terms.day_rate ? ` · $${passOn.terms.day_rate}/day incl. GST` : ""}. Open any tradie below and the slip is already filled in.</div>
          </div>
          <button onClick={() => { clearPassOn(); setPassOn(null); }} className="inline-flex items-center gap-1 px-3 py-2 border border-white/20 text-white text-xs font-bold uppercase tracking-widest hover:border-red-500 hover:text-red-500" data-testid="pass-on-dismiss"><X className="w-3.5 h-3.5" /> Start fresh instead</button>
        </div>
      )}

      <div className="mt-6 space-y-5">
        <DateRow value={availableOn} onChange={setAvailableOn} />
        <TradeChips value={trade} onChange={setTrade} />
      </div>

      {rateHint?.sweet_spot != null && (
        <div className="mt-5 border border-[#FF6A00]/40 bg-[#FF6A00]/5 p-3 flex items-center gap-3 text-sm" data-testid="rate-hint-bubble">
          <TrendingUp className="w-4 h-4 text-[#FF6A00] flex-shrink-0" />
          <div><span className="font-bold text-white">Sweet spot ${rateHint.sweet_spot}/day</span>{rateHint.low_rate != null && <span className="text-zinc-400"> — {trade.toLowerCase()}s charging ${rateHint.low_rate}–${rateHint.high_rate} land the most work.</span>}</div>
        </div>
      )}

      <div className="mt-6"><FiltersButton center={center} setCenter={setCenter} sort={sort} setSort={setSort} count={rows?.length || 0} dateLabel={availableOn ? formatDMY(availableOn) : ""} /></div>

      {rows && rows.length === 0 && (
        <div className="mt-4 border border-dashed border-white/15 p-8 text-center" data-testid="contractors-empty">
          <div className="font-display font-black text-2xl uppercase text-white">{trade ? `No ${trade.toLowerCase()}s` : "No tradies"} listed{when ? ` as free ${when}` : ""}{center ? ` near ${center}` : ""} yet</div>
          <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">{isBuilder ? "Post the job and every matching tradie in range gets a text. Or ping them directly now." : "Check back soon — tradies list their free days as they open up."}</p>
          {isBuilder && (
            <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/jobs/new" className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF6A00] text-black text-xs font-black uppercase tracking-widest hover:bg-white" data-testid="empty-post-job"><Plus className="w-4 h-4" /> Post the job</Link>
              {trade && !beaconLastSent && <button onClick={() => { setBeaconCity(center || user?.city || ""); setBeaconOpen(true); }} className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-white/20 text-white text-xs font-black uppercase tracking-widest hover:border-[#FF6A00] hover:text-[#FF6A00]" data-testid="beacon-open-btn"><Radio className="w-4 h-4" /> Ping nearby {trade.toLowerCase()}s</button>}
            </div>
          )}
          {beaconLastSent && <div className="mt-3 text-[#16A34A] font-mono-data text-sm" data-testid="beacon-sent-note">Pinged {beaconLastSent.sent} tradies · they've been notified</div>}
          {isBuilder && !trade && <div className="mt-3 text-xs text-zinc-500" data-testid="beacon-pick-trade-hint">Pick a trade above to ping everyone in that trade nearby — free, up to 5 a day.</div>}
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="contractors-cards">
        {(rows || []).map((c) => (
          <ContractorCard key={c.id} c={c} inSweet={inSweet(c)} canOffer={isBuilder}
            onOpen={() => nav(`/contractors/${c.id}`)} onOffer={() => nav(`/contractors/${c.id}`, { state: { openOffer: true } })} onPrefetch={() => prefetchRoute("/contractors/:id")} />
        ))}
      </div>

      {beaconOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="beacon-modal">
          <div className="max-w-md w-full border border-white/10 bg-[#121212] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono-data text-xs tracking-widest text-[#FF6A00]">// BEACON</div>
            <h2 className="mt-1 font-display font-black text-2xl uppercase">Ping {trade.toLowerCase()}s</h2>
            <p className="text-sm text-zinc-400 mt-1">Fires an SMS + push to every {trade.toLowerCase()} matching this city. Free · limit 5/day.</p>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">City
                <input value={beaconCity} onChange={(e) => setBeaconCity(e.target.value)} placeholder="e.g. Sydney" className="mt-1 w-full bg-black border border-white/15 focus:border-[#FF6A00] focus:outline-none px-3 py-2 text-white text-sm" data-testid="beacon-city" />
              </label>
              <div className="text-[11px] text-zinc-500 font-mono-data">Date: <span className="text-white">{availableOn ? formatDMY(availableOn) : "any day"}</span></div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Optional note
                <textarea rows={3} value={beaconMsg} onChange={(e) => setBeaconMsg(e.target.value)} placeholder="Anything they should know before they apply — pay rate, site address, urgency…" className="mt-1 w-full bg-black border border-white/15 focus:border-[#FF6A00] focus:outline-none px-3 py-2 text-white text-sm" data-testid="beacon-msg" />
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setBeaconOpen(false)} className="flex-1 border border-white/20 text-white uppercase tracking-widest text-xs font-bold py-2 hover:border-red-500 hover:text-red-500" data-testid="beacon-cancel">Cancel</button>
              <button onClick={sendBeacon} disabled={beaconSending} className="flex-1 bg-[#FF6A00] text-black uppercase tracking-widest text-xs font-black py-2 hover:bg-white disabled:opacity-40" data-testid="beacon-send">{beaconSending ? "Pinging…" : `Ping ${trade.toLowerCase()}s`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
